import { useMutation, useSuspenseQuery } from "@apollo/client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trash } from "lucide-react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Section, SectionHeader } from "@roqua/components/sections";
import { Button } from "@roqua/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@roqua/components/ui/radio-group";
import { Textarea } from "@roqua/components/ui/textarea";

import { DateSelect } from "@/components/DateSelect";
import { FormSection, FormSectionList } from "@/components/forms";
import { Sentence } from "@/components/Sentence";
import { BackButton } from "@/components/ui/BackButton";
import { ButtonWithConfirmation } from "@/components/ui/button-with-confirmation";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { gql } from "@/generated/gql";
import { NonResponse, NonResponseReasonCodeEnum } from "@/generated/gql/graphql";
import { schemaGroupForCode } from "@/models/non_response";

const Query = gql(`
  query NonResponse($dossierId: ID!, $nonResponseId: ID!) {
    dossier(id: $dossierId) {
      id
      nonResponse(id: $nonResponseId) {
        id
        nonResponseAt
        reasonCode
        reasonOther
        responses {
          id questionnaireName
        }
      }
    }
  }
`);

const UpdateMutation = gql(`
  mutation UpdateNonResponse($input: NonResponseUpdateInput!) {
    nonResponseUpdate(input: $input) {
      result {
        nonResponse {
          id
          nonResponseAt
          reasonCode
          reasonOther
        }
      }
    }
  }
`);

const DeleteMutation = gql(`
  mutation DeleteNonResponse($input: NonResponseDeleteInput!) {
    nonResponseDelete(input: $input) {
      result {
        nonResponse {
          id
        }
      }
    }
  }
`);

export const Route = createFileRoute(
  "/$organizationKey/$dossierId/non_responses/show/$nonResponseId",
)({
  component: RouteComponent,
});

type FormValues = Pick<NonResponse, "nonResponseAt" | "reasonCode" | "reasonOther">;

function RouteComponent() {
  const { t } = useTranslation("non_responses");
  const { organizationKey, dossierId, nonResponseId } = Route.useParams();
  const {
    data: {
      dossier: { nonResponse },
    },
  } = useSuspenseQuery(Query, { variables: { dossierId, nonResponseId } });
  const navigate = useNavigate();

  const methods = useForm<FormValues>({
    defaultValues: {
      nonResponseAt: nonResponse.nonResponseAt,
      reasonCode: nonResponse.reasonCode,
      reasonOther: nonResponse.reasonOther,
    },
  });
  const reasonCode = methods.watch("reasonCode");

  const [updateMutation] = useMutation(UpdateMutation);
  const [deleteMutation] = useMutation(DeleteMutation);

  const updateNonResponse = (values: FormValues) => {
    return updateMutation({
      variables: {
        input: {
          nonResponseId: nonResponse.id,
          nonResponseAt: values.nonResponseAt,
          reasonCode: values.reasonCode,
          reasonOther: values.reasonOther,
        },
      },
      onCompleted: (data) => {
        if (data?.nonResponseUpdate?.result?.nonResponse) {
          methods.reset(data.nonResponseUpdate.result.nonResponse);
        }
        toast.success(t("common:changes_saved"));
        void navigate({
          to: "/$organizationKey/$dossierId",
          params: { organizationKey, dossierId },
        });
      },
    });
  };

  const destroyThisNonResponse = async () => {
    await deleteMutation({
      variables: { input: { nonResponseId } },
      onCompleted: () => {
        toast.success(t("common:deleted"));
        void navigate({
          to: "/$organizationKey/$dossierId",
          params: { organizationKey, dossierId },
        });
      },
    });
  };

  const group = schemaGroupForCode(nonResponse.reasonCode);
  const codeOptions = group.codes.map((code) => ({
    name: <Trans t={t} i18nKey={`non_responses:reasonOptionLabel.${code}`} />,
    value: code,
  }));

  return (
    <>
      <Section variant="header">
        <SectionHeader header={t("showPage.header.title")} buttons={<BackButton />} />
      </Section>

      <Section>
        <Form {...methods}>
          <form onSubmit={methods.handleSubmit(updateNonResponse)}>
            <FormSectionList>
              <FormSection header={t("questionnaireNames")}>
                <Sentence array={nonResponse.responses.map((r) => r.questionnaireName)} />
              </FormSection>

              <FormSection header={t("nonResponse")}>
                <FormField
                  control={methods.control}
                  name="nonResponseAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("nonResponseAt")}</FormLabel>
                      <FormControl>
                        <div>
                          <DateSelect
                            name={field.name}
                            date={field.value}
                            onChange={field.onChange}
                            disabled={!group.allowCustomDate}
                          />
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={methods.control}
                  name="reasonCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t(`reasonGroupLabel.${group.key}`)}</FormLabel>
                      <FormControl>
                        <RadioGroup defaultValue={field.value ?? ""} onValueChange={field.onChange}>
                          {codeOptions.map(({ name, value }) => (
                            <FormItem key={value} className="flex items-center space-x-3 space-y-0">
                              <FormControl>
                                <RadioGroupItem value={value} />
                              </FormControl>
                              <FormLabel className="font-normal">{name}</FormLabel>
                            </FormItem>
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />

                {reasonCode === NonResponseReasonCodeEnum.DidNotInviteOtherReason && (
                  <FormField
                    control={methods.control}
                    name="reasonOther"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("reasonOther")}</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value ?? ""}
                            className="w-full rounded-md border border-gray-300 p-2"
                            rows={3}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
              </FormSection>
              <FormSection header="">
                <div className="flex flex-row justify-between">
                  <div>
                    <Button type="submit">{t("common:forms.save")}</Button>
                  </div>

                  <div>
                    <ButtonWithConfirmation
                      variant="destructive"
                      onConfirm={destroyThisNonResponse}
                    >
                      <Trash className="mr-2 h-4 w-4" />
                      {t("delete")}
                    </ButtonWithConfirmation>
                  </div>
                </div>
              </FormSection>
            </FormSectionList>
          </form>
        </Form>
      </Section>
    </>
  );
}
